import type { FC } from "react";
import { useSelector } from "react-redux";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { TooltipProvider } from "@/components/ui/tooltip";

import { useGetUserDetails } from "../../../hooks/useGetUserDetails";
import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import {
  useCustomCaseTypes,
  useLawyerFirmId
} from "../../customCaseTypes/useCustomCaseTypes";
import type { LawyerReducer } from "../../../redux/lawyer/lawyerSlice";
import { NewCaseWizard } from "./NewCaseWizard";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Preselect this client and start at the workflow question. */
  initialClientId?: string | null;
  /** Remounts the wizard so each open starts fresh. */
  session?: number | string;
};

/**
 * New Case as Linear's create modal: the wizard's questions one at a time in
 * a 640px dialog near the top of the current screen.
 */
export const NewCaseDialog: FC<Props> = ({
  open,
  onOpenChange,
  initialClientId = null,
  session
}) => {
  const {
    userDetails: { id: currentUserId }
  } = useGetUserDetails();
  const firmName = useSelector((s: LawyerReducer) =>
    lawyerSelectors.selectPracticeName(s)
  );
  const { types: customTypes } = useCustomCaseTypes(useLawyerFirmId());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="top-16 flex max-h-[calc(100vh-8rem)] translate-y-0 flex-col gap-0 overflow-hidden bg-elevated p-0 sm:max-w-160"
      >
        <TooltipProvider>
          <NewCaseWizard
            key={`${session ?? ""}:${initialClientId ?? "new"}`}
            layout="dialog"
            currentUserId={currentUserId ?? ""}
            initialClientId={initialClientId}
            firmName={firmName || null}
            customTypes={customTypes}
            onCancel={() => onOpenChange(false)}
          />
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
};
